import { useState } from 'react';
import {
    PageTitleWithToolTip,
    DataGridWithExpansionPanel,
    SubTable,
} from '../Common';
import Routes from '../../routes';
import CloseWorkOrderModal from '../WorkOrder/Components/CloseWorkOrderModal';
import AddIcon from '@mui/icons-material/Add';
import CreateIcon from '@mui/icons-material/Create';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from "../../hooks";
import { canCloseWorkOrder } from '../WorkOrder/utils';
import { select_commonCellProps, text_commonCellProps } from '../Common/DataGrid/utils';
import { getDateRangeFilterOperator } from '../Common/DataGrid/FilterComponents';
import { Box, CardHeader, IconButton, Tooltip } from '@mui/material';
import { StyledCellLink, BadgeWithLinkedText } from '../Common/DataGrid/CellComponents';
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, GridActionsCellItem } from '@mui/x-data-grid-pro';
import { useEffect } from 'react';
import { fetchFilterValues, isDesktop } from '../../utils';
import { useCallback } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Grey2 } from '../../color_utils';
import UserInfo from '../../UserInfo';

const dateRangeFilterOperator = getDateRangeFilterOperator({});
const commonSubtableCellProps = { disableColumnMenu: true, disableReorder: true, sortable: false, flex: 1 };

const OpenWorkOrders = ({
    hasNoPermission = true,
    history
}) => {
    const [modalPayload, setModalPayload] = useState({});
    const [openModal, setOpenModal] = useState(false);
    const [refreshNeeded, setRefreshNeeded] = useState(false);
    const [filters, setFilters] = useState([]);
    const [expandedRowIds, setExpandedRowIds] = useState([]);

    const navigate = useNavigate({
        pageName: 'Dashboard'
    });

    const fetchFilters = async () => {
        const filters = await fetchFilterValues(Routes.Dashboard.OpenWorkOrdersFilters());
        setFilters(filters);
    };

    useEffect(() => {
        fetchFilters();
    }, []);


    const subTableColumns = [
        {
            ...commonSubtableCellProps,
            headerName: "SERVICE EVENT ID",
            field: "idString",
            renderCell: ({ row: { id } }) => (
                <StyledCellLink
                    primaryLink
                    path={`/ServiceEvent/${id}`}
                    text={id}
                    from={window.location.pathname}
                />
            )
        },
        {
            ...commonSubtableCellProps,
            headerName: "TYPE",
            field: "type",
        },
        {
            ...commonSubtableCellProps,
            headerName: "ASSET NAME",
            field: "assetName",
        },
        {
            ...commonSubtableCellProps,
            headerName: "PRIMARY TECH",
            field: "primaryTechnicianName",
        },
        {
            ...commonSubtableCellProps,
            headerName: "SERVICE DATE",
            field: "serviceDate",
        }
    ];


    const columns = [
        {
            field: 'workOrderId',
            headerName: 'Work Order ID',
            ...text_commonCellProps,
            renderCell: ({ row: { status, isDeleted, workOrderId } }) => (
                <BadgeWithLinkedText
                    statusProperty={status}
                    text={workOrderId}
                    to={{
                        pathname: `/WorkOrder/${workOrderId}`,
                        state: {
                            from: window.location.pathname
                        }
                    }}
                    deletedProperty={isDeleted}
                />
            )
        },
        {
            field: 'locationName',
            headerName: 'Location',
            ...select_commonCellProps,
            renderCell: ({ row: { locationId, locationName } }) => {
                if (hasNoPermission || !isDesktop) {
                    return <span>{locationName}</span>;
                }
                return (
                    <StyledCellLink
                        path={`/Location/${locationId}`}
                        text={locationName}
                        from={window.location.pathname}
                    />
                );
            }
        },
        {
            field: 'workOrderName',
            headerName: 'Customer Work Order #',
            ...text_commonCellProps
        },
        {
            field: 'city',
            headerName: 'City',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'scheduledDate',
            headerName: 'Scheduled Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
        },
        {
            field: 'createdOn',
            headerName: 'Created On',
            initiallyVisible: false,
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
        },
        {
            field: 'status',
            headerName: 'Status',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'statusReason',
            headerName: 'Status Reason',
            ...select_commonCellProps
        },
        {
            field: 'servicer',
            headerName: 'Service Provider',
            ...select_commonCellProps
        },
        {
            field: 'type',
            headerName: 'Work Type',
            ...select_commonCellProps
        },
        {
            field: 'isDeleted',
            headerName: 'Deleted',
            initiallyVisible: false,
            ...select_commonCellProps,
            renderCell: ({ row: { isDeleted } }) => isDeleted
        },
        {
            field: 'dueDate',
            headerName: 'Due Date',
            initiallyVisible: false,
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
        },
        {
            field: 'regionDivision',
            headerName: 'Region/Division',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
            width: 150,
            renderCell: ({ row }) => {
                const quickActions = [];
                const pushEmptyIcon = () => quickActions.push(
                    <IconButton disabled sx={{ width: "30px", height: "30px", padding: "0px", margin: "0px" }} />
                );
                quickActions.push(
                    <GridActionsCellItem
                        key="edit"
                        icon={
                            <Tooltip title="Edit Work Order">
                                <CreateIcon sx={{ color: '#666' }} />
                            </Tooltip>
                        }
                        onClick={() => navigate.push(`/EditWorkOrder/${row.workOrderId}`, null)}
                    />
                );

                if (hasNoPermission) {
                    quickActions.push(
                        <GridActionsCellItem
                            key="create"
                            icon={
                                <Tooltip title="Create Service Event">
                                    <AddIcon sx={{ color: '#666' }} />
                                </Tooltip>
                            }
                            onClick={() => navigate.push(`/CreateServiceEvent/${row.workOrderId}`, null)}
                        />
                    );
                } else {
                    pushEmptyIcon()
                }

                if (canCloseWorkOrder(row)) {
                    quickActions.push(
                        <GridActionsCellItem
                            key="close"
                            icon={
                                <Tooltip title="Close Work Order">
                                    <CloseIcon sx={{ color: '#666' }} />
                                </Tooltip>
                            }
                            onClick={() => {
                                const payload = {
                                    ...row,
                                    ID: row.workOrderId,
                                };
                                setModalPayload(payload);
                                setOpenModal(true);
                            }}
                        />
                    );
                } else {
                    pushEmptyIcon()
                }
                if (row.hasOpenServiceEvent) {
                    quickActions.push(
                        <CustomDetailPanelToggle row={row} />
                    );
                } else { pushEmptyIcon() }
                return quickActions;
            }
        }
    ];

    const CustomDetailPanelToggle = useCallback(({ row }) => {
        const isOpen = expandedRowIds.includes(row.workOrderId);
        return (
            <Tooltip
                title={
                    isOpen
                        ? "Hide Service Events"
                        : "View Service Events"
                }
            >
                <IconButton
                    size="medium"
                    tabIndex={-1}
                    onClick={() => handleOnDetailPanelExpandedRowIdsChange(
                        isOpen
                            ? expandedRowIds.filter(id => id !== row.workOrderId)
                            : [...expandedRowIds, row.workOrderId]
                    )}
                >
                    <ExpandMoreIcon
                        sx={(theme) => ({
                            color: Grey2,
                            transform: `rotateZ(${isOpen ? 180 : 0}deg)`,
                            transition: theme.transitions.create('transform', {
                                duration: theme.transitions.duration.complex
                            }),
                        })}
                        fontSize="inherit"
                    />
                </IconButton>
            </Tooltip>
        );
    }, [expandedRowIds]);

    const getDetailPanelContent = useCallback(({ row }) => {
        if (row.hasOpenServiceEvent) {
            return (
                <div className='subtable'>
                    <SubTable
                        parentEntityId={row.id}
                        columns={subTableColumns}
                    />
                </div>
            );
        }

        return null;
    }, [expandedRowIds.length]);

    const handleOnDetailPanelExpandedRowIdsChange = (newIds) => {
        if (newIds.length > 0) {
            setExpandedRowIds([newIds[newIds.length - 1]]);
        } else {
            setExpandedRowIds([]);
        }
    }
    return (
        <>
            <PageTitleWithToolTip
                pageTitle="Open Work Orders"
                toolTipText
                fontSize="18px"
                sx={{
                    marginBottom: "24px", marginTop: "24px", display: { xs: 'block', lg: 'none' },
                    marginLeft: "16px"
                }}
            />
            <DataGridWithExpansionPanel
                    url={Routes.Dashboard.OldestOpenWorkOrders()}
                    subTableColumns={subTableColumns}
                    filterOptions={filters}
                    exportButtonURL={Routes.Dashboard.ExportOpenWorkOrders()}
                    exportButtonAlt="Export OpenWork Orders (.csv)"
                    exportFileName="OpenWorkOrders"
                    noResultText="No open work orders found..."
                    noResultText2="All work orders are completed or you can create new ones."
                    columns={columns}
                    refresh={refreshNeeded}
                    getRowId={(row) => row.workOrderId}
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={() => "auto"}
                    detailPanelExpandedRowIds={expandedRowIds}
                    columnDependencies={expandedRowIds}
                    onDetailPanelExpandedRowIdsChange={handleOnDetailPanelExpandedRowIdsChange}
                    dataGridSx={{
                        '& .MuiButton-root[aria-label*="Export"]': {
                            display: { xs: 'none', lg: 'inline-flex' }
                        }
                    }}
                    headerContent={
                        <CardHeader
                            title={
                                <PageTitleWithToolTip
                                    pageTitle={"Open Work Orders"}
                                />
                            }
                            sx={{
                                "& .MuiCardHeader-action": { display: "flex" },
                                "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" },
                                display: { xs: 'none', lg: 'block' }
                            }}
                        />
                    }
                />

            <CloseWorkOrderModal
                payload={modalPayload}
                modalOpen={openModal}
                onClose={() => setOpenModal(false)}
                refreshRequired={() => setRefreshNeeded(!refreshNeeded)}
            />
        </>
    )
};

export default OpenWorkOrders;
